import homepage from '../../../portal-content/pages/homepage.md?raw';
import onboarding from '../../../portal-content/pages/onboarding.md?raw';
import mcpVsDirectRest from '../../../portal-content/pages/mcp-vs-direct-rest.md?raw';
import usersApi from '../../../portal-content/pages/quickstarts/users-api.md?raw';
import mcpApi from '../../../portal-content/pages/quickstarts/mcp-api.md?raw';
import commercialDirectoryApi from '../../../portal-content/pages/quickstarts/commercial-directory-api.md?raw';
import commercialPipelineApi from '../../../portal-content/pages/quickstarts/commercial-pipeline-api.md?raw';
import commercialAgreementsApi from '../../../portal-content/pages/quickstarts/commercial-agreements-api.md?raw';
import customerAssetsApi from '../../../portal-content/pages/quickstarts/customer-assets-api.md?raw';
import troubleshooting from '../../../portal-content/pages/troubleshooting-playbook.md?raw';
import regionalGuidance from '../../../portal-content/pages/regional-guidance-sla-rate-limits.md?raw';

export interface DocumentationPage {
  slug: string;
  title: string;
  source: string;
  group: 'Start' | 'Guides' | 'Quickstarts' | 'Operations';
  summary: string;
  content: string;
}

export const DOCUMENTATION_PAGES: DocumentationPage[] = [
  {
    slug: 'home',
    title: 'Welcome to iasset One',
    source: 'pages/homepage.md',
    group: 'Start',
    summary: 'Your integration workspace for APIs, agent tools, onboarding, and operational guidance.',
    content: homepage
  },
  {
    slug: 'onboarding',
    title: 'Request API User Access',
    source: 'pages/onboarding.md',
    group: 'Start',
    summary: 'Request an iasset.com API user, call bootstrap, and make the first authenticated request.',
    content: onboarding
  },
  {
    slug: 'mcp-vs-direct-rest',
    title: 'MCP vs Direct REST',
    source: 'pages/mcp-vs-direct-rest.md',
    group: 'Guides',
    summary: 'Choose the right integration pattern for orchestration or direct domain access.',
    content: mcpVsDirectRest
  },
  {
    slug: 'quickstart-users-api',
    title: 'Quickstart: Users API',
    source: 'pages/quickstarts/users-api.md',
    group: 'Quickstarts',
    summary: 'Bootstrap customer API-user access and validate identity setup.',
    content: usersApi
  },
  {
    slug: 'quickstart-mcp-api',
    title: 'Quickstart: MCP API',
    source: 'pages/quickstarts/mcp-api.md',
    group: 'Quickstarts',
    summary: 'Start with the MCP orchestration surface.',
    content: mcpApi
  },
  {
    slug: 'quickstart-commercial-directory-api',
    title: 'Quickstart: Commercial Directory API',
    source: 'pages/quickstarts/commercial-directory-api.md',
    group: 'Quickstarts',
    summary: 'Use entities and products as customer-facing reference data.',
    content: commercialDirectoryApi
  },
  {
    slug: 'quickstart-commercial-pipeline-api',
    title: 'Quickstart: Commercial Pipeline API',
    source: 'pages/quickstarts/commercial-pipeline-api.md',
    group: 'Quickstarts',
    summary: 'Work with opportunities, quotes, and pipeline records.',
    content: commercialPipelineApi
  },
  {
    slug: 'quickstart-commercial-agreements-api',
    title: 'Quickstart: Commercial Agreements API',
    source: 'pages/quickstarts/commercial-agreements-api.md',
    group: 'Quickstarts',
    summary: 'Retrieve contracts and agreement-linked entities.',
    content: commercialAgreementsApi
  },
  {
    slug: 'quickstart-customer-assets-api',
    title: 'Quickstart: Customer Assets API',
    source: 'pages/quickstarts/customer-assets-api.md',
    group: 'Quickstarts',
    summary: 'Query customer asset inventory and churn-risk data.',
    content: customerAssetsApi
  },
  {
    slug: 'troubleshooting',
    title: 'Troubleshooting Playbook',
    source: 'pages/troubleshooting-playbook.md',
    group: 'Operations',
    summary: 'Diagnose 401, 403, 429, and 5xx responses before opening a support ticket.',
    content: troubleshooting
  },
  {
    slug: 'regional-guidance-sla-rate-limits',
    title: 'Regional Guidance, SLA, and Rate Limits',
    source: 'pages/regional-guidance-sla-rate-limits.md',
    group: 'Operations',
    summary: 'Choose the right endpoint, region, and reliability pattern.',
    content: regionalGuidance
  }
];

const pagesBySlug = new Map(DOCUMENTATION_PAGES.map((page) => [page.slug, page]));
const slugsBySource = new Map(DOCUMENTATION_PAGES.map((page) => [normalizeSourcePath(page.source), page.slug]));

export function getDocumentationPage(slug: string): DocumentationPage {
  return pagesBySlug.get(slug) ?? DOCUMENTATION_PAGES[0];
}

export function resolveDocumentationHref(href: string, currentPage: DocumentationPage): string {
  if (/^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith('#')) {
    return href;
  }

  const withoutAnchor = href.split('#')[0];
  if (!withoutAnchor.endsWith('.md')) {
    return href;
  }

  const currentDirectory = currentPage.source.includes('/')
    ? currentPage.source.slice(0, currentPage.source.lastIndexOf('/') + 1)
    : '';
  const normalized = normalizeSourcePath(`${currentDirectory}${withoutAnchor}`);
  const slug = slugsBySource.get(normalized) ?? slugsBySource.get(normalizeSourcePath(withoutAnchor));
  return slug ? `#docs/${slug}` : href;
}

function normalizeSourcePath(path: string): string {
  const parts: string[] = [];
  for (const segment of path.replace(/\\/g, '/').split('/')) {
    if (!segment || segment === '.') {
      continue;
    }

    if (segment === '..') {
      parts.pop();
      continue;
    }

    parts.push(segment);
  }

  return parts.join('/');
}
